import { unauthenticatedApiClient } from './unauthenticated-client';
import { apiClient } from './client';
import type { ApiResponse } from '@/types';
import type { LoginInput } from '@/lib/zod';

export interface LoginResponse {
  user: {
    userId: string;
    email: string;
    fullName?: string;
  };
  tokens: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  };
}

export interface SignupRequest {
  email: string;
  password: string;
  role: 'GYM_OWNER';
}

export interface SignupResponse {
  message: string;
  user?: {
    userId: string;
    email: string;
  };
  userId?: string;
  email?: string;
  requiresVerification?: boolean;
  upgraded?: boolean;
}

/**
 * Login API call - unauthenticated endpoint
 */
export const loginApi = async (data: LoginInput): Promise<LoginResponse> => {
  const response = await unauthenticatedApiClient.post<ApiResponse<LoginResponse>>(
    '/v1/auth/signin',
    data
  );
  return response.data.data;
};

export interface GoogleAuthRequest {
  idToken: string;
  role: 'GYM_OWNER';
}

// Google auth response uses the same structure as login
export type GoogleAuthResponse = LoginResponse;

/**
 * Google auth API call - unauthenticated endpoint
 */
export const googleAuthApi = async (data: GoogleAuthRequest): Promise<GoogleAuthResponse> => {
  const response = await unauthenticatedApiClient.post<ApiResponse<GoogleAuthResponse>>(
    '/v1/auth/google',
    data
  );
  return response.data.data;
};

/**
 * Signup API call - unauthenticated endpoint
 */
export const signupApi = async (data: SignupRequest): Promise<SignupResponse> => {
  const response = await unauthenticatedApiClient.post<ApiResponse<SignupResponse>>(
    '/v1/auth/signup',
    data
  );
  return response.data.data;
};

export interface VerifyEmailOtpRequest {
  email: string;
  otp: string;
}

export interface VerifyEmailOtpResponse {
  user: {
    userId: string;
    email: string;
    fullName?: string;
  };
  tokens: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  };
}

/**
 * Verify email OTP API call - unauthenticated endpoint
 */
export const verifyEmailOtpApi = async (
  data: VerifyEmailOtpRequest
): Promise<VerifyEmailOtpResponse> => {
  const response = await unauthenticatedApiClient.post<ApiResponse<VerifyEmailOtpResponse>>(
    '/v1/auth/verify-email-otp',
    data
  );
  return response.data.data;
};

export interface ResendOtpRequest {
  email: string;
  type: 'EMAIL_VERIFICATION';
}

export interface ResendOtpResponse {
  message: string;
}

/**
 * Resend OTP API call - unauthenticated endpoint
 */
export const resendOtpApi = async (data: ResendOtpRequest): Promise<ResendOtpResponse> => {
  const response = await unauthenticatedApiClient.post<ApiResponse<ResendOtpResponse>>(
    '/v1/auth/resend-otp',
    data
  );
  return response.data.data;
};

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  message: string;
}

/**
 * Forgot password API call - unauthenticated endpoint
 */
export const forgotPasswordApi = async (
  data: ForgotPasswordRequest
): Promise<ForgotPasswordResponse> => {
  const response = await unauthenticatedApiClient.post<ApiResponse<ForgotPasswordResponse>>(
    '/v1/auth/forgot-password',
    data
  );
  return response.data.data;
};

export interface ResetPasswordOtpRequest {
  email: string;
  otp: string;
  newPassword: string;
}

export interface ResetPasswordOtpResponse {
  message: string;
}

/**
 * Reset password with OTP API call - unauthenticated endpoint
 */
export const resetPasswordOtpApi = async (
  data: ResetPasswordOtpRequest
): Promise<ResetPasswordOtpResponse> => {
  const response = await unauthenticatedApiClient.post<ApiResponse<ResetPasswordOtpResponse>>(
    '/v1/auth/reset-password-otp',
    data
  );
  return response.data.data;
};

export interface RefreshTokenRequest {
  refreshToken: string;
}

// Server response format: { data: { tokens: { accessToken, refreshToken } } }
export interface RefreshTokenApiResponse {
  tokens: {
    accessToken: string;
    refreshToken?: string;
  };
}

// What we return from the API function
export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken?: string;
}

/**
 * Refresh access token - unauthenticated endpoint
 */
export const refreshTokenApi = async (data: RefreshTokenRequest): Promise<RefreshTokenResponse> => {
  const response = await unauthenticatedApiClient.post<ApiResponse<RefreshTokenApiResponse>>(
    '/v1/auth/refresh',
    data
  );
  return response.data.data.tokens;
};

export interface SignoutRequest {
  refreshToken: string;
}

export interface SignoutResponse {
  message: string;
}

/**
 * Signout API call - invalidates the refresh token on the server
 * Uses authenticated client as signout requires valid access token
 */
export const signoutApi = async (data: SignoutRequest): Promise<SignoutResponse> => {
  const response = await apiClient.post<ApiResponse<SignoutResponse>>('/v1/auth/signout', data);
  return response.data.data;
};
